import React from "react";
import { Helmet } from "react-helmet-async";
import "../CookiePolicy.css";

const CookiePolicyEN = () => {
  return (
    <div className="cookie-policy-page">
      <Helmet>
        <title>Cookie Policy | ProgenX Privacy and Data Protection 2026</title>
        <meta name="description" content="ProgenX Cookie Policy. Detailed information about cookies used on our website, personal data protection, KVKK compliance, and cookie management. Learn how to manage cookies." />
        <meta name="keywords" content="progenx cookie policy, cookie usage, privacy policy, kvkk, personal data protection, cookie management, data security" />
        <meta property="og:title" content="Cookie Policy | ProgenX" />
        <meta property="og:description" content="ProgenX website cookie usage and privacy policy." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenx.com.tr/en/cookie-policy" />
        <link rel="canonical" href="https://progenx.com.tr/en/cookie-policy" />
        <link rel="alternate" hreflang="tr" href="https://progenx.com.tr/cerez-politikasi" />
        <link rel="alternate" hreflang="en" href="https://progenx.com.tr/en/cookie-policy" />
      </Helmet>
      <div className="container">
        <div className="cookie-policy-header">
          <h1>Cookie Policy</h1>
          <p className="subtitle">ProgenX Education and Information Technologies Inc.</p>
        </div>

        <div className="cookie-policy-content">
          <div className="intro-section">
            <h2>Website Privacy and Cookie Policy</h2>
            <p>
              As ProgenX Education and Information Technologies Inc. ("ProgenX"), we respect your privacy and attach importance to your data security. In this context, we process and protect the personal data of visitors ("visitor") to the website operated by ProgenX in accordance with Law No. 6698 on the Protection of Personal Data ("Law" or "KVKK").
            </p>
            <p>
              With this Cookie Policy, we would like to inform you about the purposes for which personal data obtained during the use of cookies are processed, what types of cookies we use, and how you can manage these cookies. For cookies other than mandatory cookies we use, explicit consent of visitors is obtained through the cookie management tool, and visitors are provided with the opportunity to change their consent at any time.
            </p>
          </div>

          <div className="table-of-contents">
            <h3>Table of Contents</h3>
            <ol>
              <li>Who is ProgenX?</li>
              <li>What is a Cookie and Why is it Used?</li>
              <li>For What Purposes Can Cookies Be Used on the Website?</li>
              <li>What Types of Cookies Do We Use and in What Ways?</li>
              <li>For What Purposes and Legal Reasons Do We Process Your Personal Data with Cookies?</li>
              <li>Cookie Management</li>
              <li>To Whom, For What Purpose, and For What Legal Reason Are Your Personal Data Transferred?</li>
              <li>How Can You Prevent the Use of Cookies?</li>
              <li>What are your rights regarding your personal data?</li>
              <li>Effectiveness of the Policy</li>
              <li>Contact</li>
            </ol>
          </div>

          <div className="policy-section">
            <h2>1. Who is ProgenX?</h2>
            <p>
              ProgenX Education and Information Technologies Inc. is ProgenX Education and Information Technologies Inc. with Tax Identification Number 7331258939, operating at Üniversiteler Mah. İhsan Doğramacı Bul. No: 31 İç Kapı No: 20 Çankaya/Ankara METU Technopark ETKİM Building. Detailed information for contact is provided under Article 11 of this Website Privacy and Cookie Policy.
            </p>
          </div>

          <div className="policy-section">
            <h2>2. What is a Cookie and Why is it Used?</h2>
            <p>
              A Cookie (Cookie) is a type of identification file left on a computer by any website. Session information and similar data are stored in cookie files. When a site that uses cookies is visited, that site may send a request to the browser through which access is made to leave one or more cookies on the hard disk. Cookies do not contain personal data such as name, age, address, gender belonging to people who visit the site. To learn more about cookies, please visit www.aboutcookies.org and www.allaboutcookies.org.
            </p>
          </div>

          <div className="policy-section">
            <h2>3. For What Purposes Can Cookies Be Used on the Website?</h2>
            <ul>
              <li>Performing functions necessary for the website to work</li>
              <li>Personalizing visits and experiences, targeting, accurate and purpose-oriented advertising and promotional activities</li>
              <li>Ensuring that website and components function correctly, easily, and more efficiently</li>
              <li>Improving performance and user experience by analyzing information such as website, resource usage, access information, visitor count, and platform detection</li>
              <li>The company's need for your personal data such as your IP address in order to fulfill its legal and contractual obligations, especially those arising from Law No. 5651 on the Regulation of Publications Made on the Internet and Combating Crimes Committed Through These Publications</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>4. What Types of Cookies Do We Use and in What Ways?</h2>
            <p>
              We use different types of cookies on the Website. These are mandatory cookies required for the Website to work, function cookies, analysis/performance cookies, and targeting/advertising cookies.
            </p>
            
            <h3>4.1. Cookie Types by Usage:</h3>
            <div className="cookie-types">
              <h4>4.1.1. Mandatory Cookies:</h4>
              <p>These are cookies that are absolutely necessary for the Website to work properly. These cookies are needed for system management and prevention of fraudulent transactions, and if blocked, the Website will not work as required.</p>
              
              <h4>4.1.2. Function Cookies:</h4>
              <p>These are cookies used to provide you with a more advanced and easy user experience. For example, they remember your previous preferences and allow you to easily access some content on the Website.</p>
              
              <h4>4.1.3. Analysis/Performance Cookies:</h4>
              <p>These cookies enable us to analyze and understand the operation of the Website and allow us to improve the Website by interacting with you.</p>
              
              <h4>4.1.4. Targeting/Advertising Cookies:</h4>
              <p>These cookies are used to identify and present content that may interest you, including advertising content.</p>
            </div>

            <h3>4.2. Cookie Types by Storage Duration:</h3>
            <div className="cookie-duration">
              <h4>4.2.1. Persistent Cookies:</h4>
              <p>Cookies that persist on a person's computer until a certain date or until deleted by the user.</p>
              
              <h4>4.2.2. Session Cookies:</h4>
              <p>These cookies are used to separate the user's visit into sessions and do not collect data from the user.</p>
            </div>

            <h3>4.3. Cookie Types by Ownership:</h3>
            <div className="cookie-ownership">
              <h4>4.3.1. First-Party Cookies:</h4>
              <p>Placed directly by the internet page visited by the visitor.</p>
              
              <h4>4.3.2. Third-Party Cookies:</h4>
              <p>Placed by a third party different from the internet site visited by the Website visitor.</p>
            </div>
          </div>

          <div className="policy-section">
            <h2>5. For What Purposes and Legal Reasons Do We Process Your Personal Data with Cookies?</h2>
            <p>
              Cookies, depending on their types, generally collect data about browsing and usage preferences on the device you access the Website from. This data includes all information about the pages you access, the services and products you examine, and your navigation on the Website.
            </p>
          </div>

          <div className="policy-section">
            <h2>6. Cookie Management</h2>
            <p>
              To prevent cookies other than mandatory cookies, set the buttons in the cookie management panel to open or closed according to your preference and click the "Save Changes" button. Refresh the page to activate the settings.
            </p>
          </div>

          <div className="policy-section">
            <h2>7. How Can You Prevent the Use of Cookies?</h2>
            <p>
              Although the use of cookies enables the Website to provide better service, if you wish, you can prevent the use of cookies. To prevent the use of cookies, you need to change the settings of the internet browser you use.
            </p>
            
            <div className="browser-instructions">
              <h3>Browser Settings:</h3>
              <h4>Google Chrome:</h4>
              <ol>
                <li>Open Chrome on your computer</li>
                <li>Click More → Settings in the top right</li>
                <li>Click Advanced at the bottom</li>
                <li>Click Content settings under "Privacy and security"</li>
                <li>Click Cookies</li>
              </ol>

              <h4>Mozilla Firefox:</h4>
              <ol>
                <li>Click the Firefox Menu button and select Options</li>
                <li>Select the Privacy & Security panel</li>
                <li>Go to the History section</li>
                <li>Manage cookies</li>
              </ol>

              <h4>Safari:</h4>
              <ol>
                <li>Select Safari → Preferences</li>
                <li>Click Privacy</li>
                <li>Manage Website Data</li>
              </ol>
            </div>
          </div>

          <div className="policy-section">
            <h2>8. What are your rights regarding your personal data?</h2>
            <p>
              We have listed the rights you have under Article 11 of the Personal Data Protection Law below:
            </p>
            <ul>
              <li>Learning whether your personal data is processed</li>
              <li>Requesting information from us if your personal data has been processed</li>
              <li>Learning the purpose of processing your personal data and whether they are used in accordance with their purpose</li>
              <li>Knowing the third parties to whom your personal data is transferred domestically or abroad</li>
              <li>Requesting correction of your personal data if it has been processed incompletely or incorrectly</li>
              <li>Requesting deletion or destruction of your personal data if the reasons requiring processing have disappeared</li>
              <li>Objecting to a result that emerges against you as a result of analysis of processed data exclusively through automated systems</li>
              <li>Requesting compensation for damages if you have suffered damage due to illegal processing of your personal data</li>
            </ul>
          </div>

          <div className="policy-section">
            <h2>9. Effectiveness of the Policy</h2>
            <p>
              This Policy enters into force on the date of publication. If all or certain articles of the Policy are renewed, the effective date of the Policy is revised.
            </p>
            <p className="last-updated">
              <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US')}
            </p>
          </div>

          <div className="policy-section">
            <h2>10. Contact</h2>
            <p>
              You can always submit your requests regarding your rights in writing or by using your email address in accordance with the Regulation on the Procedures and Principles of Application to the Data Controller, to the addresses specified below.
            </p>
            <div className="contact-info">
              <p><strong>Data Controller:</strong> ProgenX Education and Information Technologies Inc.</p>
              <p><strong>Email:</strong> info@progenx.com.tr<br /></p>
              <p><strong>Website:</strong> www.progenx.com.tr</p>
              <p><strong>Address:</strong> Ankara, Turkey<br /></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyEN;
